import { useState } from "react";

import { toISODateTime } from "common/utils/dates";
import { Feature, SubscriptionFrequency, UserSubscriptionAction } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Calendar from "client/common/components/calendars/Calendar";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";

import { useUpdateSubscriptionMutation } from "../../hooks";
import { ClientSubscription } from "../../types";
import { minSelectableDeliveryDate } from "../../utils";
import SubscriptionFrequencySelect from "../SubscriptionFrequencySelect";

export type SubscriptionResumeDrawerProps = {
  subscription: ClientSubscription;
};

const SubscriptionResumeDrawer = ({ subscription }: SubscriptionResumeDrawerProps) => {
  const { close } = useCurrentDrawer();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const {
    mutate: updateSubscription,
    isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  // v2 delivery dates cannot fall inside the min lead-time window; v1 next order dates
  // only need to be in the future.
  const [scheduleDate, setScheduleDate] = useState(
    isEdbEnabled ? minSelectableDeliveryDate() : new Date(),
  );
  const [frequencyValues, setFrequencyValues] = useState<SubscriptionFrequency>(
    subscription.is_active_frequency_valid
      ? subscription.active_frequency
      : subscription.recommended_frequency,
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Resume autoship"
        description={
          isEdbEnabled
            ? "Enable your Autoship again by selecting your next estimated delivery date and frequency"
            : "Enable your Autoship again by selecting your next order date and frequency"
        }
        onClose={close}
      />
      <DrawerLayout.Form
        size="l"
        canSubmit={true}
        onSubmit={() =>
          updateSubscription({
            action: UserSubscriptionAction.Resume,
            ...(isEdbEnabled
              ? { expected_delivery_by: toISODateTime(scheduleDate) }
              : { next_order_date: toISODateTime(scheduleDate) }),
            ...frequencyValues,
          })
        }
        submitText="Resume autoship"
        isLoading={isLoading}
        error={error}
        fieldErrors={[
          error?.errors.action,
          isEdbEnabled ? error?.errors.expected_delivery_by : error?.errors.next_order_date,
          error?.errors.frequency,
          error?.errors.interval_unit,
        ]}
      >
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>
            {isEdbEnabled
              ? "Select your next estimated delivery date"
              : "Select your next order date"}
          </DrawerLayout.Form.Label>
          <Calendar
            disabledDays={{ before: isEdbEnabled ? minSelectableDeliveryDate() : new Date() }}
            selectedDays={scheduleDate}
            onSelect={setScheduleDate}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select your order frequency</DrawerLayout.Form.Label>
          <SubscriptionFrequencySelect
            subscription={subscription}
            value={frequencyValues}
            onSelect={setFrequencyValues}
          />
        </DrawerLayout.Form.Group>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionResumeDrawer;
